import { dateStringSerializer } from "@megaron/date-string";
import { HttpAction } from "@megaron/http-service";
import { Serializers, SerializerValue } from "@megaron/serializers";
import { uuidSerializer } from "@megaron/uuid";

import { productCodeStatusSerializer } from "./codeStatus";

export const productCodeDtoSerializer = Serializers.object({
  id: Serializers.string,
  status: productCodeStatusSerializer,
  pointValue: Serializers.decimal.nullable(),
  productId: Serializers.string.nullable(),
  massKg: Serializers.decimal.nullable(),
});

export type ProductCodeDto = SerializerValue<typeof productCodeDtoSerializer>;

const checkProductCode = HttpAction({
  path: "/productCodes/id/:codeId",
  method: "get",
  paramsSerializer: Serializers.object({
    codeId: Serializers.string,
  }),
  qsSerializer: Serializers.object<{ key?: string }>({
    key: Serializers.string.optional(),
  }),
  valueSerializer: productCodeDtoSerializer,
  errorSerializer: Serializers.stringOneOf("CodeNotFound"),
});

export const checkOrRedeemProductCodeDtoSerializer = Serializers.object({
  code: productCodeDtoSerializer,
  redeemedPoints: Serializers.decimal.nullable(),
});

export type CheckOrRedeemProductCodeDto = SerializerValue<typeof checkOrRedeemProductCodeDtoSerializer>;

const checkOrRedeemProductCode = HttpAction({
  path: "/productCodes/id/:codeId/redeem",
  method: "post",
  paramsSerializer: Serializers.object({
    codeId: Serializers.string,
  }),
  bodySerializer: Serializers.object({
    key: Serializers.string.nullable(),
  }),
  valueSerializer: checkOrRedeemProductCodeDtoSerializer,
  errorSerializer: Serializers.stringOneOf(
    "InvalidCode",
    "InvalidKey",
    "CodeInactive",
    "CodeAlreadyRedeemed",
    "CodeExpired",
  ),
});

const generateProductCode = HttpAction({
  path: "/productCodes/generate",
  method: "post",
  requiresAuth: true as const,
  valueSerializer: Serializers.object({
    id: Serializers.string,
    key: Serializers.string,
  }),
});

const generateProductCodes = HttpAction({
  path: "/productCodes/generateBatch",
  method: "post",
  requiresAuth: true as const,
  bodySerializer: Serializers.object({
    quantity: Serializers.integer,
    groupNames: Serializers.array(Serializers.string),
  }),
  valueSerializer: Serializers.array(
    Serializers.object({
      id: Serializers.string,
      key: Serializers.string,
    }),
  ),
});

const activateProductCode = HttpAction({
  path: "/productCodes/id/:codeId/activate",
  method: "post",
  requiresAuth: true as const,
  paramsSerializer: Serializers.object({
    codeId: Serializers.string,
  }),
  bodySerializer: Serializers.object({
    pointValue: Serializers.decimal,
    productId: Serializers.string,
    expiresAfterDays: Serializers.integer,
    massKg: Serializers.decimal,
  }),
});

const activateProductCodeGroups = HttpAction({
  path: "/productCodeGroups/activate",
  method: "post",
  requiresAuth: true as const,
  bodySerializer: Serializers.object({
    groupNames: Serializers.array(Serializers.string),
    pointValue: Serializers.decimal,
    productId: Serializers.string,
    expiresAfterDays: Serializers.integer,
    massKg: Serializers.decimal,
  }),
});

const expireProductCodes = HttpAction({
  path: "/productCodes/expire",
  method: "post",
  requiresAuth: true as const,
});

const findProductCodes = HttpAction({
  path: "/productCodes/id/:codeId/inspect",
  requiresAuth: true as const,
  method: "get",
  paramsSerializer: Serializers.object({
    codeId: Serializers.string,
  }),
  valueSerializer: Serializers.object({
    id: Serializers.string,
    status: productCodeStatusSerializer,
    pointValue: Serializers.decimal.nullable(),
    productId: Serializers.string.nullable(),
    massKg: Serializers.decimal.nullable(),
    activatedAt: Serializers.datetime.nullable(),
    activatedByUuid: Serializers.string.nullable(),
    generatedAt: Serializers.datetime,
    generatedByUuid: Serializers.string,
    redeemedAt: Serializers.datetime.nullable(),
    redeemedByUuid: Serializers.string.nullable(),
    destroyedAt: Serializers.datetime.nullable(),
    destroyedByUuid: Serializers.string.nullable(),
    expiredAt: Serializers.datetime.nullable(),
    expiredByUuid: Serializers.string.nullable(),
    nominalExpirationDate: dateStringSerializer.nullable(),
    groupNames: Serializers.array(Serializers.string),
  }).array(),
  errorSerializer: Serializers.stringOneOf("CodeNotFound"),
});

const grantProductCodeRedemptions = HttpAction({
  path: "/productCodes/grantRedemptions",
  method: "post",
  bodySerializer: Serializers.object({
    groupNames: Serializers.array(Serializers.string),
    userId: uuidSerializer,
    ...Serializers.object({
      dryRun: Serializers.boolean,
      notifyUser: Serializers.boolean,
    }).partial().fields,
  }),
  valueSerializer: Serializers.object({
    codeCount: Serializers.integer,
    productIds: Serializers.string.array(),
    totalPointValue: Serializers.decimal,
  }),
  requiresAuth: true as const,
  errorSerializer: Serializers.stringOneOf("CodeNotRedeemable", "LimitExceeded", "NoCodesFound"),
});

const generateExampleProductCodeCsv = HttpAction({
  path: "/productCodes/generateExampleCsv",
  method: "get",
  resultWrapper: false,
  // requiresAuth: true as const,
  qsSerializer: Serializers.object({
    quantity: Serializers.integer,
  }).partial(),
  valueSerializer: Serializers.string,
  errorSerializer: Serializers.stringOneOf("FileAlreadyExists"),
});

const issueCodeGenerationOrder = HttpAction({
  path: "/productCodes/generationOrders/:orderId",
  method: "post",
  paramsSerializer: Serializers.object({
    orderId: Serializers.string,
  }),
  requiresAuth: true as const,
  bodySerializer: Serializers.object({
    quantity: Serializers.integer,
    groupName: Serializers.string,
    validUntil: Serializers.datetime,
  }),
});

const codeGenerationOrderSerializer = Serializers.object({
  id: Serializers.string,
  quantity: Serializers.integer,
  groupName: Serializers.string,
  validUntil: Serializers.datetime,
  createdAt: Serializers.datetime,
  createdBy: Serializers.string,
  fulfilledAt: Serializers.datetime.nullable(),
  fulfilledByIpAddress: Serializers.string.nullable(),
});

export type CodeGenerationOrder = SerializerValue<typeof codeGenerationOrderSerializer>;

const codeGenerationOrdersQuery = HttpAction({
  path: "/productCodes/generationOrders",
  method: "get",
  requiresAuth: true as const,
  qsSerializer: Serializers.object({
    limit: Serializers.integer,
    offset: Serializers.integer,
    orderId: Serializers.string.array(),
    groupName: Serializers.string,
  }).partial(),
  valueSerializer: codeGenerationOrderSerializer.array(),
});

const fulfillCodeGenerationOrderCsv = HttpAction({
  path: "/productCodes/generationOrders/:orderId/csv",
  method: "get",
  errorSerializer: Serializers.stringOneOf(
    "OrderNotFound",
    "OrderExpired",
    "OrderAlreadyFulfilled",
    "IpAddressNotAllowed",
    "NotWritable",
  ),
  resultWrapper: false,
  requiresAuth: false as const,
  paramsSerializer: Serializers.object({
    orderId: Serializers.string,
  }),
  valueSerializer: Serializers.string,
});

export const productCodeActions = {
  activateProductCode,
  activateProductCodeGroups,
  /**
   * @deprecated Use checkOrRedeemProductCode
   */
  checkProductCode,
  checkOrRedeemProductCode,
  generateProductCode,
  generateProductCodes,
  expireProductCodes,
  findProductCodes,
  grantProductCodeRedemptions,
  generateExampleProductCodeCsv,
  issueCodeGenerationOrder,
  codeGenerationOrdersQuery,
  fulfillCodeGenerationOrderCsv,
};
